import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class SecureCookieService {
  private readonly DEFAULT_EXPIRY_DAYS = 7;

  // Cache the cookie availability result to avoid repeated testing
  private cookieAvailabilityCache: boolean | null = null;

  /**
   * Sets a secure cookie with all necessary security flags
   */
  setCookie(name: string, value: string, days?: number): void {
    try {
      const expiryDays = days ?? this.DEFAULT_EXPIRY_DAYS;
      const expires = new Date(Date.now() + expiryDays * 24 * 60 * 60 * 1000);
      const cookieValue = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; Secure; SameSite=Strict; Path=/`;

      // Check cookie size limit (4KB per cookie)
      if (cookieValue.length > 4096) {
        throw new Error(`Cookie ${name} exceeds 4KB size limit`);
      }

      document.cookie = cookieValue;
    } catch (error) {
      console.error(`Failed to set cookie ${name}:`, error);
      throw new Error(`Failed to set secure cookie: ${error}`);
    }
  }

  /**
   * Sets a secure cookie with expiration timestamp tracking
   */
  setCookieWithExpirationTracking(name: string, value: string, expirationCookieName: string, days?: number): void {
    try {
      const expiryDays = days ?? this.DEFAULT_EXPIRY_DAYS;
      const expires = new Date(Date.now() + expiryDays * 24 * 60 * 60 * 1000);

      // Set the main cookie
      this.setCookie(name, value, days);

      // Set the expiration tracking cookie
      this.setCookie(expirationCookieName, expires.getTime().toString(), days);
    } catch (error) {
      console.error(`Failed to set cookie with expiration tracking ${name}:`, error);
      throw new Error(`Failed to set secure cookie with expiration tracking: ${error}`);
    }
  }

  /**
   * Retrieves a cookie value by name
   */
  getCookie(name: string): string | null {
    try {
      const nameEQ = name + '=';
      const cookies = document.cookie.split(';');

      for (let i = 0; i < cookies.length; i++) {
        let cookie = cookies[i].trim();
        if (cookie.indexOf(nameEQ) === 0) {
          const value = cookie.substring(nameEQ.length);
          return decodeURIComponent(value);
        }
      }
      return null;
    } catch (error) {
      console.error(`Failed to get cookie ${name}:`, error);
      return null;
    }
  }

  /**
   * Deletes a cookie by setting its expiration to the past
   */
  deleteCookie(name: string): void {
    try {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; Secure; SameSite=Strict; Path=/`;
    } catch (error) {
      console.error(`Failed to delete cookie ${name}:`, error);
    }
  }

  /**
   * Clears multiple cookies by name
   */
  clearCookies(cookieNames: string[]): void {
    cookieNames.forEach(cookieName => {
      this.deleteCookie(cookieName);
    });
  }

  /**
   * Checks if a cookie-based expiration timestamp has passed
   */
  isCookieExpired(expirationCookieName: string): boolean {
    try {
      const expiresStr = this.getCookie(expirationCookieName);
      if (!expiresStr) {
        return true; // No expiration info means expired
      }

      const expiresTime = parseInt(expiresStr, 10);
      if (isNaN(expiresTime)) {
        return true; // Invalid date format means expired
      }
      return Date.now() >= expiresTime;
    } catch (error) {
      console.error('Failed to parse cookie expiration:', error);
      return true; // Assume expired on error
    }
  }

  /**
   * Checks if cookies are available and functional with caching for performance
   * @param forceRecheck - Optional flag to bypass cache and retest
   */
  areCookiesAvailable(forceRecheck: boolean = false): boolean {
    // Return cached result if available and not forcing recheck
    if (this.cookieAvailabilityCache !== null && !forceRecheck) {
      return this.cookieAvailabilityCache;
    }

    try {
      // First, use the browser's native cookie support detection
      if (typeof navigator !== 'undefined' && navigator.cookieEnabled === false) {
        this.cookieAvailabilityCache = false;
        return false;
      }

      // If navigator.cookieEnabled is true or undefined, perform a lightweight test
      // Use document.cookie directly for minimal impact
      const testName = '_cc_test';
      const testValue = '1';

      // Set a minimal test cookie
      document.cookie = `${testName}=${testValue}; Path=/`;

      // Check if it was set
      const isAvailable = document.cookie.indexOf(`${testName}=${testValue}`) !== -1;

      // Clean up immediately
      if (isAvailable) {
        document.cookie = `${testName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; Path=/; Secure; SameSite=Strict`;
      }

      // Cache the result
      this.cookieAvailabilityCache = isAvailable;
      return isAvailable;
    } catch (error) {
      console.error('Cookies are not available:', error);
      this.cookieAvailabilityCache = false;
      return false;
    }
  }

}
